/*
  File:        BookingCard.tsx
  Path:        /Users/larrymclean/CascadeProjects/aquorix-frontend/src/features/dashboard/components/BookingCard.tsx
  Description:
    P10 Bookings List modern card.
    - Presentational only.
    - Reflects backend booking truth.
    - Shows operational, payment, and refund axes without collapsing state.

  Author: AQUORIX Team
  Created: 2026-03-15
  Version: 0.3.0

  Change Log (append-only):
    - 2026-03-15 - v0.1.0
      - Initial developer-ready skeleton for BookingCard MVP
    - 2026-06-19 - v0.2.0
      - Align card status language with Booking Lifecycle doctrine.
      - Add three-axis booking/payment/refund display.
      - Surface Cancelled + Paid as Funds Held, not Refunded.
    - 2026-06-19 - v0.3.0
      - Replace legacy vertical card with approved P10 compact booking queue card.
*/

import React from "react";

export type BookingCardData = {
  booking_id: string | number;
  guest_name: string;
  guest_email?: string | null;
  guest_phone?: string | null;

  booking_status: string;
  payment_status: string;
  ui_status: string;
  refund_status?: string | null;
  refunded_at?: string | null;
  refund_reason?: string | null;

  total_pax?: number | null;
  item_count?: number | null;

  first_session_at_local?: string | null;
  last_session_at_local?: string | null;
  hold_expires_at?: string | null;
  paid_at?: string | null;

  booking_items_summary?: string | null;
  sites_summary?: string | null;
  roster?: string[];

  payment_currency?: string | null;
  payment_amount?: string | null;
  payment_amount_minor?: string | number | null;

  stripe_charge_currency?: string | null;
  stripe_charge_amount_minor?: string | number | null;

  source?: string | null;
  created_at?: string | null;
};

type BookingCardProps = {
  booking: BookingCardData;
  onOpen?: (booking: BookingCardData) => void;
};

function labelize(value?: string | null): string {
  if (!value) return "—";
  return String(value).replace(/_/g, " ");
}

function initials(name?: string | null): string {
  const parts = String(name || "")
    .trim()
    .split(/\s+/)
    .filter(Boolean);

  if (parts.length === 0) return "—";
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();

  return `${parts[0][0]}${parts[parts.length - 1][0]}`.toUpperCase();
}

function formatDisplayDateTime(value?: string | null): string {
  if (!value) return "—";

  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return String(value);

  const mon = d.toLocaleString("en-US", { month: "short" });
  const dd = String(d.getDate()).padStart(2, "0");
  const hh = String(d.getHours()).padStart(2, "0");
  const mi = String(d.getMinutes()).padStart(2, "0");

  return `${mon} ${dd} · ${hh}:${mi}`;
}

function formatMoney(
  currency?: string | null,
  amount?: string | null,
  amountMinor?: string | number | null
): string {
  const cur = String(currency || "JOD").toUpperCase();

  if (amount && currency) return `${amount} ${cur}`;

  if (amountMinor !== undefined && amountMinor !== null) {
    const n = Number(amountMinor);
    if (Number.isFinite(n)) {
      const divisor = cur === "JOD" ? 1000 : 100;
      return `${(n / divisor).toFixed(2)} ${cur}`;
    }
  }

  if (currency) return `— ${cur}`;
  return "—";
}

function isHoldExpiringSoon(value?: string | null): boolean {
  if (!value) return false;

  const expires = new Date(value).getTime();
  if (!Number.isFinite(expires)) return false;

  const diffMs = expires - Date.now();
  return diffMs > 0 && diffMs <= 24 * 60 * 60 * 1000;
}

function formatHoldCountdown(value?: string | null): string | null {
  if (!value) return null;

  const expires = new Date(value).getTime();
  if (!Number.isFinite(expires)) return null;

  const diffMs = expires - Date.now();

  if (diffMs <= 0) return "hold expired";

  const hours = Math.floor(diffMs / (60 * 60 * 1000));
  const minutes = Math.floor((diffMs % (60 * 60 * 1000)) / (60 * 1000));

  if (hours > 0) return `hold ${hours}h ${minutes}m`;
  return `hold ${minutes}m`;
}

function getAxisClass(axis: "booking" | "payment" | "refund", value?: string | null): keyof typeof styles {
  const v = String(value || "").toLowerCase();

  if (axis === "refund") {
    if (v === "refunded") return "axisRefunded";
    if (v === "refund_pending") return "axisUnpaid";
    return "axisNeutral";
  }

  if (axis === "booking") {
    if (v === "confirmed") return "axisConfirmed";
    if (v === "pending") return "axisPending";
    if (v === "cancelled") return "axisCancelled";
  }

  if (axis === "payment") {
    if (v === "paid") return "axisPaid";
    if (v === "unpaid") return "axisUnpaid";
  }

  return "axisNeutral";
}

function getCardMode(booking: BookingCardData): keyof typeof styles | null {
  const ui = String(booking.ui_status || "").toLowerCase();
  const bookingStatus = String(booking.booking_status || "").toLowerCase();
  const paymentStatus = String(booking.payment_status || "").toLowerCase();
  const refundStatus = String(booking.refund_status || "").toLowerCase();

  if (ui === "paid_manual_review") {
    return "cardNeeds";
  }

  if (bookingStatus === "cancelled" && paymentStatus === "paid" && refundStatus !== "refunded") {
    return "cardNeeds";
  }

  if (bookingStatus === "pending" && paymentStatus === "unpaid" && isHoldExpiringSoon(booking.hold_expires_at)) {
    return "cardHold";
  }

  return null;
}

function getOperationalNote(booking: BookingCardData): string | null {
  const ui = String(booking.ui_status || "").toLowerCase();
  const bookingStatus = String(booking.booking_status || "").toLowerCase();
  const paymentStatus = String(booking.payment_status || "").toLowerCase();
  const refundStatus = String(booking.refund_status || "").toLowerCase();

  if (refundStatus === "refunded") return "Money collected, then returned.";
  if (refundStatus === "refund_pending") return "Refund is in progress.";
  if (bookingStatus === "cancelled" && paymentStatus === "paid") return "Seat released. Funds still held.";
  if (bookingStatus === "cancelled") return "Seat released.";
  if (ui === "paid_manual_review") {
    return "Paid after hold or needs seat review.";
  }
  if (bookingStatus === "confirmed" && paymentStatus === "unpaid") {
    return "Paid after hold or needs seat review.";
  }
  if (bookingStatus === "confirmed" && paymentStatus === "unpaid") {
    return "Confirmed on operator trust.";
  }

  return null;
}

function getSessionWindow(first?: string | null, last?: string | null): string {
  const a = formatDisplayDateTime(first);
  const b = formatDisplayDateTime(last);

  if (!last || last === first) return a;
  return `${a} → ${b}`;
}

export default function BookingCard({ booking, onOpen }: BookingCardProps) {
  const mode = getCardMode(booking);
  const note = getOperationalNote(booking);
  const holdCountdown = formatHoldCountdown(booking.hold_expires_at);
  const roster = Array.isArray(booking.roster) ? booking.roster : [];
  const visibleRoster = roster.slice(0, 3);
  const extraRoster = Math.max(roster.length - visibleRoster.length, 0);

  function handleOpen() {
    if (onOpen) onOpen(booking);
  }

  return (
    <button
      type="button"
      onClick={handleOpen}
      style={{
        ...styles.card,
        ...(mode ? styles[mode] : null),
      }}
      aria-label={`Open booking ${booking.booking_id}`}
    >
      <div style={styles.row}>
        <div style={styles.left}>
          <div style={styles.topLine}>
            <span style={styles.bookingId}>#{booking.booking_id}</span>
            <span style={styles.guestName}>{booking.guest_name || "Guest"}</span>
            <span style={styles.sourceBadge}>{String(booking.source || "—").toUpperCase()}</span>
          </div>

          <div style={styles.metaLine}>
            <span>{booking.total_pax ?? 0} divers</span>
            <span style={styles.dot}>·</span>
            <span>{booking.item_count ?? 0} sessions</span>
            <span style={styles.dot}>·</span>
            <span style={styles.mono}>{getSessionWindow(booking.first_session_at_local, booking.last_session_at_local)}</span>
          </div>

          <div style={styles.sites}>{booking.sites_summary || booking.booking_items_summary || "—"}</div>

          {visibleRoster.length > 0 ? (
            <div style={styles.roster}>
              {visibleRoster.map((name, index) => (
                <span key={`${name}-${index}`} style={styles.rosterAvatar}>
                  {initials(name)}
                </span>
              ))}
              {extraRoster > 0 ? <span style={styles.rosterMore}>+{extraRoster}</span> : null}
            </div>
          ) : null}
        </div>

        <div style={styles.right}>
          <div style={styles.axisStack}>
            <span style={{ ...styles.axis, ...styles[getAxisClass("booking", booking.booking_status)] }}>
              <span style={styles.axisDot} />
              {labelize(booking.booking_status)}
            </span>

            <span style={{ ...styles.axis, ...styles[getAxisClass("payment", booking.payment_status)] }}>
              <span style={styles.axisDot} />
              {labelize(booking.payment_status)}
            </span>

            {booking.refund_status ? (
              <span style={{ ...styles.axis, ...styles[getAxisClass("refund", booking.refund_status)] }}>
                <span style={styles.axisDot} />
                {labelize(booking.refund_status)}
              </span>
            ) : null}
          </div>

          <div style={styles.money}>
            {formatMoney(booking.payment_currency, booking.payment_amount, booking.payment_amount_minor)}
          </div>

          {holdCountdown && String(booking.payment_status || "").toLowerCase() === "unpaid" ? (
            <div style={styles.holdCountdown}>⏱ {holdCountdown}</div>
          ) : note ? (
            <div style={styles.when}>{note}</div>
          ) : booking.paid_at ? (
            <div style={styles.when}>Paid {formatDisplayDateTime(booking.paid_at)}</div>
          ) : null}
        </div>
      </div>
    </button>
  );
}

const styles: Record<string, React.CSSProperties> = {
  card: {
    width: "100%",
    textAlign: "left",
    background: "var(--surface)",
    border: "1px solid var(--line)",
    borderRadius: 12,
    padding: 0,
    cursor: "pointer",
    display: "block",
    overflow: "hidden",
    boxShadow: "0 1px 2px rgba(15, 39, 51, 0.03)",
  },

  cardNeeds: {
    borderLeft: "4px solid var(--rare)",
  },

  cardHold: {
    borderLeft: "4px solid var(--gold)",
  },

  row: {
    display: "grid",
    gridTemplateColumns: "1fr auto",
    gap: 12,
    padding: "12px 16px",
  },

  left: {
    minWidth: 0,
  },

  right: {
    minWidth: 132,
    textAlign: "right",
    display: "flex",
    flexDirection: "column",
    alignItems: "flex-end",
    gap: 4,
  },

  topLine: {
    display: "flex",
    alignItems: "center",
    gap: 8,
    flexWrap: "wrap",
  },

  bookingId: {
    fontFamily: "'IBM Plex Mono', 'SF Mono', Menlo, Consolas, monospace",
    fontSize: 11,
    fontWeight: 800,
    color: "var(--slate)",
  },

  guestName: {
    fontSize: 16,
    fontWeight: 900,
    color: "var(--ink)",
    lineHeight: 1.2,
  },

  sourceBadge: {
    fontFamily: "'IBM Plex Mono', 'SF Mono', Menlo, Consolas, monospace",
    fontSize: 9.5,
    fontWeight: 800,
    letterSpacing: "0.06em",
    color: "var(--slate)",
    background: "var(--page)",
    border: "1px solid var(--line)",
    borderRadius: 3,
    padding: "2px 7px",
  },

  metaLine: {
    fontSize: 12,
    color: "var(--ink)",
    marginTop: 4,
    display: "flex",
    gap: 6,
    flexWrap: "wrap",
    alignItems: "center",
  },

  dot: {
    color: "var(--line)",
  },

  mono: {
    fontFamily: "'IBM Plex Mono', 'SF Mono', Menlo, Consolas, monospace",
    fontSize: 12,
  },

  sites: {
    fontSize: 12,
    color: "var(--slate)",
    marginTop: 3,
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  },

  roster: {
    display: "flex",
    gap: 5,
    marginTop: 6,
    flexWrap: "wrap",
  },

  rosterAvatar: {
    width: 22,
    height: 22,
    borderRadius: "50%",
    background: "var(--deep-aqua)",
    color: "var(--surface)",
    fontSize: 9.5,
    fontWeight: 800,
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    fontFamily: "'IBM Plex Mono', 'SF Mono', Menlo, Consolas, monospace",
  },

   rosterMore: {
    width: 24,
    height: 24,
    borderRadius: "50%",
    background: "var(--page)",
    color: "var(--slate)",
    border: "1px solid var(--line)",
    fontSize: 9.5,
    fontWeight: 800,
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    fontFamily: "'IBM Plex Mono', 'SF Mono', Menlo, Consolas, monospace",
  },

  axisStack: {
    display: "flex",
    flexDirection: "column",
    gap: 3,
    alignItems: "flex-end",
  },

  axis: {
    display: "inline-flex",
    alignItems: "center",
    gap: 5,
    fontSize: 10,
    fontWeight: 900,
    letterSpacing: "0.03em",
    borderRadius: 3,
    padding: "2px 8px",
    textTransform: "uppercase",
    fontFamily: "'IBM Plex Mono', 'SF Mono', Menlo, Consolas, monospace",
  },

  axisDot: {
    width: 6,
    height: 6,
    borderRadius: "50%",
    background: "currentColor",
  },

  axisConfirmed: {
    background: "var(--go-bg)",
    color: "var(--go-deep)",
  },

  axisPending: {
    background: "var(--full-bg)",
    color: "var(--full)",
  },

  axisCancelled: {
    background: "var(--full-bg)",
    color: "var(--full)",
  },

  axisPaid: {
    background: "var(--go-bg)",
    color: "var(--go-deep)",
  },

  axisUnpaid: {
    background: "var(--gold-bg)",
    color: "var(--gold-deep)",
  },

  axisRefunded: {
    background: "var(--rare-bg)",
    color: "var(--rare-deep)",
  },

  axisNeutral: {
    background: "var(--full-bg)",
    color: "var(--full)",
  },

  money: {
    fontFamily: "'IBM Plex Mono', 'SF Mono', Menlo, Consolas, monospace",
    fontSize: 15,
    fontWeight: 900,
    color: "var(--ink)",
    marginTop: 1,
  },

  holdCountdown: {
    fontFamily: "'IBM Plex Mono', 'SF Mono', Menlo, Consolas, monospace",
    fontSize: 11,
    fontWeight: 800,
    color: "var(--gold-deep)",
    background: "var(--gold-bg)",
    borderRadius: 3,
    padding: "2px 7px",
  },

  when: {
    fontSize: 11.5,
    color: "var(--slate)",
    maxWidth: 180,
  },
};